import { IDropdownOption, Toggle } from '@fluentui/react';
import { forwardRef, HTMLAttributes, Ref, useEffect, useRef, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { toggleStylesEditor } from '../../themes/themes';
import { useClassNames } from '../../utils/hooks';
import { Dropdown } from '../Dropdown/Dropdown';
import { IDropdownProps } from '../Dropdown/Dropdown';
import { dropdownStylesEditor } from '../Dropdown/Dropdown.styles';
import { TextField } from '../TextField/TextField';
import { textFieldStylesEditor } from '../TextField/TextField.styles';
import { editorStylesDefault, IEditorClassNames, IEditorStyles } from './Editor.styles';

export enum FieldType {
  text = 'text',
  dropdown = 'dropdown',
  toggle = 'toggle',
}

export interface IEditorProps extends IComponent<IEditorStyles>, HTMLAttributes<HTMLDivElement> {
  targetEl?: HTMLElement;
  hidden?: boolean;
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  left?: number;
  top?: number;
  fieldType?: FieldType;
  value?: any;
  'data-test'?: string;
  meta?: { rowIndex?: number; fieldName?: string; item?: any; data?: any }; // if it is a cell of a grid, it can have row index
  fieldProps?: { [key: string]: any };
  onChange?: (value?: any, meta?: any) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const setFocus = (fieldType: FieldType, refField: React.RefObject<HTMLElement>) => {
  // after the state(value) is updated
  setTimeout(() => {
    const el = refField.current as HTMLInputElement;
    if (el) {
      if (el.selectionStart) {
        el.setSelectionRange(0, 0);
      }
      if (el.focus) {
        if (fieldType === FieldType.dropdown) {
          (el as any).focus(true);
        } else {
          el.focus();
        }
      }
    }
  });
};

const renderField = (
  fieldType: FieldType,
  fieldProps: { [key: string]: any } = {},
  value: any,
  setValue: React.Dispatch<any>,
  refField: React.RefObject<any>,
  onChange?: (value?: any, meta?: { [key: string]: any }) => void,
  meta?: { [key: string]: any },
  onKeyDown?: (e: React.KeyboardEvent) => void
) => {
  switch (fieldType) {
    case FieldType.dropdown:
      return (
        <Dropdown
          componentRef={refField}
          {...(fieldProps as IDropdownProps)}
          styles={fieldProps.styles ? fieldProps.styles : dropdownStylesEditor}
          selectedKey={value}
          onKeyDown={onKeyDown}
          onChange={(e: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            if (fieldProps.onChange) {
              fieldProps.onChange(e, option, index);
            }
            if (option) {
              setValue(option.key);
              if (onChange) {
                onChange(option.key, meta);
              }
            }
          }}
        />
      );
    case FieldType.toggle:
      return (
        <Toggle
          componentRef={refField}
          {...fieldProps}
          styles={fieldProps.styles ? fieldProps.styles : toggleStylesEditor}
          checked={value}
          onKeyDown={onKeyDown}
          onChange={(e: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean | undefined) => {
            if (fieldProps.onChange) {
              fieldProps.onChange(e, checked);
            }
            if (checked !== undefined) {
              setValue(checked);
              if (onChange) {
                onChange(checked, meta);
              }
            }
          }}
        />
      );
    default:
      return (
        <TextField
          ref={refField}
          {...fieldProps}
          styles={fieldProps.styles ? fieldProps.styles : textFieldStylesEditor}
          value={value}
          onKeyDown={onKeyDown}
          onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string | undefined) => {
            if (fieldProps.onChange) {
              fieldProps.onChange(e, newValue);
            }
            if (newValue !== undefined) {
              setValue(newValue);
              if (onChange) {
                onChange(newValue, meta);
              }
            }
          }}
        />
      );
  }
};

export const Editor = forwardRef((props: IEditorProps, ref: Ref<HTMLDivElement>) => {
  const {
    styles,
    className,
    fieldType = FieldType.text,
    fieldProps,
    width = 0,
    height = 0,
    minHeight = 0,
    minWidth = 0,
    left,
    top,
    hidden = true,
    value: fieldValue = '',
    meta,
    targetEl,
    onChange,
    onKeyDown,
    ...elementProps
  } = props;
  const refField = useRef(null);
  const [value, setValue] = useState(fieldValue);
  const classNames = useClassNames<IEditorStyles, IEditorClassNames>('Editor', editorStylesDefault, styles, className);
  // when open new editor
  useEffect(() => {
    if (!targetEl || hidden) {
      setValue('');
    } else {
      setValue(fieldValue);
      setFocus(fieldType, refField);
    }
  }, [targetEl, hidden, fieldValue, fieldType]);
  return (
    <div
      {...elementProps}
      ref={ref}
      className={classNames.root}
      style={{
        width: Math.max(minWidth, width),
        height: Math.max(minHeight, height),
        left,
        top,
        display: hidden ? 'none' : '',
      }}
      data-test={props['data-test'] || 'editor'}
    >
      {targetEl && !hidden && renderField(fieldType, fieldProps, value, setValue, refField, onChange, meta, onKeyDown)}
    </div>
  );
});
